import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Truck, CheckCircle, Clock, AlertTriangle, ChevronRight } from 'lucide-react';
import MessengerTrackingMap from '../components/MessengerTrackingMap';

const MessengerTrackingPage = () => {
    const { token } = useAuth();
    const [viewMode, setViewMode] = useState('dashboard'); // 'dashboard' | 'map'
    const [dashboardData, setDashboardData] = useState([]);
    const [selectedMessenger, setSelectedMessenger] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [routeData, setRouteData] = useState([]);

    const fetchRouteData = async () => {
        try {
            const today = new Date().toISOString().split('T')[0];
            const res = await fetch(`/api/tracking/route?messengerId=${selectedMessenger}&date=${today}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setRouteData(data.data);
            }
        } catch (error) {
            console.error('Error fetching route:', error);
        }
    };

    useEffect(() => {
        if (viewMode === 'map' && selectedMessenger) {
            fetchRouteData();
        }
    }, [viewMode, selectedMessenger]);

    useEffect(() => {
        if (viewMode === 'dashboard') {
            fetchDashboardData();
            const interval = setInterval(fetchDashboardData, 60000); // Auto-refresh 1 min
            return () => clearInterval(interval);
        }
    }, [viewMode]);

    const fetchDashboardData = async () => {
        try {
            const res = await fetch('/api/tracking/global-summary', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setDashboardData(data.data);
                setLastUpdated(new Date());
            }
        } catch (error) {
            console.error('Error fetching dashboard:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectMessenger = (messengerId) => {
        setSelectedMessenger(messengerId);
        setViewMode('map');
    };

    const StatusBadge = ({ count, type }) => {
        const colors = {
            pending: 'bg-blue-100 text-blue-800',
            in_progress: 'bg-yellow-100 text-yellow-800',
            done: 'bg-green-100 text-green-800',
            delayed: 'bg-red-100 text-red-800'
        };
        const icons = {
            pending: <Clock size={14} className="mr-1" />,
            in_progress: <Truck size={14} className="mr-1" />,
            done: <CheckCircle size={14} className="mr-1" />,
            delayed: <AlertTriangle size={14} className="mr-1" />
        };

        return (
            <div className={`flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${colors[type]}`}>
                {icons[type]}
                {count}
            </div>
        );
    };

    // --- RENDER DASHBOARD ---
    if (viewMode === 'dashboard') {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Monitor de Mensajeros en Vivo</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Actualizado: {lastUpdated.toLocaleTimeString()}
                        </p>
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex justify-center p-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {dashboardData.map((item) => {
                            const total = item.stats.total_active + item.stats.done_today;
                            const progress = total > 0 ? Math.round((item.stats.done_today / total) * 100) : 0;

                            return (
                                <div
                                    key={item.messenger.id}
                                    className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
                                    onClick={() => handleSelectMessenger(item.messenger.id)}
                                >
                                    {/* Header Card */}
                                    <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                                                {item.messenger.name ? item.messenger.name.charAt(0) : '?'}
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-gray-800">{item.messenger.name}</h3>
                                                <div className="flex gap-2 mt-1">
                                                    <StatusBadge count={item.stats.done_today} type="done" />
                                                    {item.stats.delayed > 0 && <StatusBadge count={item.stats.delayed} type="delayed" />}
                                                </div>
                                            </div>
                                        </div>
                                        <ChevronRight className="text-gray-400" />
                                    </div>

                                    {/* Body Card */}
                                    <div className="p-4">
                                        {/* Progress Bar */}
                                        <div className="mb-4">
                                            <div className="flex justify-between text-xs mb-1 text-gray-500">
                                                <span>Progreso Hoy</span>
                                                <span className="font-semibold">{progress}%</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                <div className="bg-green-500 h-2.5 rounded-full" style={{ width: `${progress}%` }}></div>
                                            </div>
                                        </div>

                                        {/* Activity Lists */}
                                        <div className="space-y-3">
                                            {item.orders.in_progress.length > 0 && (
                                                <div className="bg-yellow-50 p-2 rounded-md border border-yellow-100">
                                                    <p className="text-xs font-bold text-yellow-800 mb-1 flex items-center">
                                                        <Truck size={12} className="mr-1" /> En Reparto ({item.orders.in_progress.length})
                                                    </p>
                                                    {item.orders.in_progress.slice(0, 3).map(o => (
                                                        <div key={o.id} className="text-xs text-gray-600 truncate">
                                                            #{o.order_number} - {o.customer_name}
                                                        </div>
                                                    ))}
                                                    {item.orders.in_progress.length > 3 && (
                                                        <div className="text-[10px] text-gray-400 italic">...y {item.orders.in_progress.length - 3} más</div>
                                                    )}
                                                </div>
                                            )}

                                            {item.orders.pending.length > 0 && (
                                                <div className="bg-blue-50 p-2 rounded-md border border-blue-100">
                                                    <p className="text-xs font-bold text-blue-800 mb-1 flex items-center">
                                                        <Clock size={12} className="mr-1" /> Pendientes ({item.orders.pending.length})
                                                    </p>
                                                    <div className="text-xs text-gray-500">
                                                        {item.orders.pending.length} pedidos asignados o listos.
                                                    </div>
                                                </div>
                                            )}

                                            {total === 0 && (
                                                <p className="text-center text-xs text-gray-400 py-2">Sin actividad hoy</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }



    // --- RENDER MAP DETAIL ---
    if (viewMode === 'map') {
        const messengerName = dashboardData.find(m => m.messenger.id === selectedMessenger)?.messenger.name || 'Mensajero';
        const pendingOrders = routeData.filter(p => p.type === 'pending');

        return (
            <div className="h-screen flex flex-col">
                <div className="bg-white border-b px-4 py-3 flex items-center gap-4 shadow-sm z-10">
                    <button
                        onClick={() => setViewMode('dashboard')}
                        className="text-gray-600 hover:text-blue-600 flex items-center gap-1 text-sm font-medium"
                    >
                        ← Volver al Monitor
                    </button>
                    <div className="h-6 w-px bg-gray-300"></div>
                    <h2 className="font-bold text-gray-800">
                        Detalle de Ruta: {messengerName}
                    </h2>
                </div>

                <div className="flex-1 flex overflow-hidden">
                    {/* Left Panel: Pending Orders List */}
                    <div className="w-80 bg-white border-r overflow-y-auto">
                        <div className="p-4 bg-gray-50 border-b">
                            <h3 className="font-bold text-gray-700 flex items-center">
                                <Clock size={16} className="mr-2 text-blue-600" />
                                Pendientes ({pendingOrders.length})
                            </h3>
                            <p className="text-xs text-gray-500 mt-1">
                                Estos pedidos no tienen ubicación GPS aún.
                            </p>
                        </div>
                        <div className="divide-y">
                            {pendingOrders.map(order => (
                                <div key={order.order_id} className="p-4 hover:bg-gray-50">
                                    <div className="flex justify-between items-start mb-1">
                                        <span className="font-bold text-sm text-gray-800">#{order.order_number}</span>
                                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                            {order.delivery_status}
                                        </span>
                                    </div>
                                    <p className="text-sm font-medium text-gray-700">{order.customer_name}</p>
                                    <p className="text-xs text-gray-500 mt-1 truncate">{order.customer_address}</p>
                                    <p className="text-xs font-semibold text-green-600 mt-2">
                                        Total: ${new Intl.NumberFormat('es-CO').format(order.total_amount)}
                                    </p>
                                </div>
                            ))}
                            {pendingOrders.length === 0 && (
                                <div className="p-8 text-center text-gray-400 text-sm">
                                    No hay pedidos pendientes
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Panel: Map */}
                    <div className="flex-1 relative">
                        <MessengerTrackingMap
                            routePoints={routeData}
                            messengerId={selectedMessenger}
                            date={new Date().toISOString().split('T')[0]} // Default to today for detail view
                        />
                    </div>
                </div>
            </div>
        );
    }

    return null; // Fallback
};

export default MessengerTrackingPage;
